// SAB-575: shared Google/Apple OAuth entry, used by /signup, /login, and the
// desktop inline signup card. Intent survives the provider round-trip because
// it's baked into redirectTo (a full app route), not component state.
//
// FLAG: the 'google'/'apple' providers must be enabled in the Supabase dashboard
// (Auth → Providers) for this to succeed; until then it surfaces a toast. Provider
// config is a separate setup step tracked in the SAB-575 report.
import { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { OAuthProvider } from '@/components/auth/SocialAuthButtons';

export function useOAuthSignIn(targetPath: string) {
  const { toast } = useToast();
  const [pending, setPending] = useState(false);

  const failToast = (provider: OAuthProvider, message?: string) =>
    toast({
      title: `Couldn't continue with ${provider === 'google' ? 'Google' : 'Apple'}`,
      description: message ?? 'Please try again.',
      variant: 'destructive',
    });

  const signInWith = async (provider: OAuthProvider) => {
    setPending(true);

    // Build 6 native branch (reachable only on 1.2.0+, since SocialAuthButtons is
    // version-gated): the SDK cannot navigate the webview to the provider, so open the
    // auth URL in the system browser and return via the custom scheme, which
    // NativeUrlHandler exchanges. skipBrowserRedirect makes signInWithOAuth hand us the
    // URL instead of navigating. Intent rides in ?next= on the scheme redirect.
    if (Capacitor.isNativePlatform()) {
      const redirectTo = `com.sabotagefantasy.app://auth-callback?next=${encodeURIComponent(targetPath)}`;
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo, skipBrowserRedirect: true },
      });
      if (error || !data?.url) {
        setPending(false);
        failToast(provider, error?.message);
        return;
      }
      try {
        const { Browser } = await import('@capacitor/browser');
        await Browser.open({ url: data.url });
      } catch {
        setPending(false);
        failToast(provider, 'Could not open the sign-in browser.');
      }
      // On success the appUrlOpen handler completes it; leave `pending` set.
      return;
    }

    // Web: unchanged. The browser navigates to the provider and returns to targetPath.
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${window.location.origin}${targetPath}` },
    });
    if (error) {
      setPending(false);
      failToast(provider, `${error.message} — this sign-in provider may not be enabled yet.`);
    }
    // On success the browser navigates to the provider; leave `pending` set.
  };

  return { signInWith, pending };
}
